import { useEffect, useRef, useState } from 'react';
import { geoAPI, getApiErrorMessage } from '@matix/api-client';
import type { GeoAddress } from '@matix/types';
import { useLang } from '../../lib/language';
import { MapPinIcon, SearchIcon } from '../../components/icons';
import { pickName } from './addressMatch';

/**
 * Address lookup — the highlighted block at the top of every address card.
 *
 * Two modes, one control:
 *   - Saudi Arabia: the National Address short code (العنوان الوطني, e.g.
 *     RFHA6515). It is a real, stored field (ShortAddressCode), so what the
 *     cashier types here is saved as well as searched. A complete, valid code
 *     is looked up automatically after a short pause; anything else needs the
 *     button.
 *   - Everywhere else: a free-text "find this address" box. Nothing is stored —
 *     the box only drives the search, and the resolved parts land in the normal
 *     fields below.
 *
 * The lookup itself goes through pos-api (/api/v1/geo), never straight to
 * Google: the API key must not ship inside a PWA that lives on every branch
 * tablet. See pos-api/src/pos/geo/geo.service.ts for the provider chain.
 *
 * A resolved address is never written straight over the card. Free-text parts
 * (street, building number, postal code) are filled, values that would fail the
 * Saudi format checks are skipped rather than pasted in to fail on save, and
 * city/district are matched against the tenant's own GL_Governorate / GL_Area
 * rows by CityAreaFields — Google's name for a district is not a foreign key.
 */

/** Saudi short address: 4 letters + 4 digits. */
const KSA_SHORT_CODE = /^[A-Za-z]{4}\d{4}$/;
const AUTO_LOOKUP_DELAY_MS = 700;

export type LookupStatus = 'idle' | 'loading' | 'ok' | 'notfound' | 'error';

/**
 * Whether the server has a Google key is a per-deployment constant, and a
 * customer form can hold several address cards. Ask once per session and let
 * every panel share the answer.
 */
let configPromise: Promise<boolean> | null = null;
function lookupAvailable(): Promise<boolean> {
  if (!configPromise) {
    configPromise = geoAPI
      .config()
      .then((c) => c.enabled)
      .catch(() => false);
  }
  return configPromise;
}

export function AddressLookupPanel({
  isKsa,
  regionCode,
  /** The stored short code (KSA only). */
  code,
  onCodeChange,
  codeError,
  disabled,
  /** Fired with the resolved address; the card decides what to apply. */
  onResolved,
  /** Cleared/failed lookup — the card drops the pin. */
  onCleared,
  result,
}: {
  isKsa: boolean;
  regionCode: string | null;
  code: string;
  onCodeChange: (v: string) => void;
  codeError?: string;
  disabled: boolean;
  onResolved: (addr: GeoAddress) => void;
  onCleared: () => void;
  /** The last resolved address for this card, so the panel can show the pin. */
  result: GeoAddress | null;
}) {
  const { t: tr, lang } = useLang();

  const [available, setAvailable] = useState<boolean | null>(null);
  const [status, setStatus] = useState<LookupStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  /** Free-text query — non-KSA mode only. Not persisted anywhere. */
  const [query, setQuery] = useState('');

  // The value we last sent to the server. Seeded on mount with whatever the
  // card already holds, so opening a saved customer doesn't fire a lookup for
  // an address that is already filled in — the cashier asks for that with the
  // button.
  const lastSearched = useRef<string>(isKsa ? code.trim().toUpperCase() : '');

  // Is the lookup configured on this server at all?
  useEffect(() => {
    let cancelled = false;
    void lookupAvailable().then((ok) => {
      if (!cancelled) setAvailable(ok);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const lookupOn = available === true;
  const term = isKsa ? code.trim().toUpperCase() : query.trim();
  const canSearch = lookupOn && !disabled && term.length >= 3;

  async function search(value: string): Promise<void> {
    const v = value.trim();
    if (!v) return;
    lastSearched.current = isKsa ? v.toUpperCase() : v;
    setStatus('loading');
    setError(null);
    try {
      const addr = await geoAPI.resolve(v, regionCode);
      if (!addr) {
        setStatus('notfound');
        onCleared();
        return;
      }
      setStatus('ok');
      onResolved(addr);
    } catch (err) {
      setStatus('error');
      setError(getApiErrorMessage(err));
      onCleared();
    }
  }

  // Auto-search a complete Saudi code. Only for KSA: a free-text box has no
  // "this is finished" signal, so that one always waits for the button.
  useEffect(() => {
    if (!isKsa || available !== true || disabled) return;
    const v = code.trim().toUpperCase();
    if (!KSA_SHORT_CODE.test(v) || v === lastSearched.current) return;
    const timer = setTimeout(() => void search(v), AUTO_LOOKUP_DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, isKsa, available, disabled, regionCode]);

  // With no key configured, the free-text box has no reason to exist — it only
  // ever drives a search. The National Address code is a *stored field*, so it
  // stays put and simply loses its button: a tenant without Google Maps must
  // still be able to record العنوان الوطني.
  if (!isKsa && !lookupOn) return null;

  const label = isKsa
    ? tr({ en: 'National address', ar: 'العنوان الوطني' })
    : tr({ en: 'Find address on the map', ar: 'ابحث عن العنوان على الخريطة' });

  return (
    <div className="addr-geo">
      <div className="addr-geo-head">
        <span className="addr-geo-badge">
          <MapPinIcon size={13} />
          {label}
        </span>
        <span className="addr-geo-hint">
          {!lookupOn
            ? tr({ en: '4 letters + 4 digits', ar: '٤ أحرف + ٤ أرقام' })
            : isKsa
              ? tr({
                  en: '4 letters + 4 digits — we fill the rest',
                  ar: '٤ أحرف + ٤ أرقام — وسنكمل باقي الحقول',
                })
              : tr({
                  en: 'Type a street or place — we fill the rest',
                  ar: 'اكتب شارعًا أو مكانًا — وسنكمل باقي الحقول',
                })}
        </span>
      </div>

      <div className="addr-geo-row">
        <input
          type="text"
          className={`addr-geo-input${codeError ? ' has-error' : ''}`}
          value={isKsa ? code : query}
          onChange={(e) => {
            if (isKsa) {
              // Stored uppercase — the short code is printed uppercase
              // everywhere and a lowercase copy reads as a different address.
              onCodeChange(e.target.value.toUpperCase());
            } else {
              setQuery(e.target.value);
            }
            if (status !== 'loading') setStatus('idle');
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            if (canSearch) void search(term);
          }}
          placeholder={isKsa ? 'RFHA6515' : tr({ en: 'e.g. King Fahd Rd, Riyadh', ar: 'مثال: طريق الملك فهد، الرياض' })}
          maxLength={isKsa ? 8 : 200}
          spellCheck={false}
          autoComplete="off"
          disabled={disabled}
          aria-label={label}
          aria-invalid={codeError ? true : undefined}
        />
        {lookupOn && (
          <button
            type="button"
            className="addr-geo-btn"
            onClick={() => void search(term)}
            disabled={!canSearch || status === 'loading'}
          >
            {status === 'loading' ? (
              tr({ en: 'Searching…', ar: 'جارٍ البحث…' })
            ) : (
              <>
                <SearchIcon size={14} />
                {tr({ en: 'Look up', ar: 'بحث' })}
              </>
            )}
          </button>
        )}
      </div>

      {codeError && <div className="add-field-error">{codeError}</div>}

      {status === 'notfound' && (
        <div className="addr-geo-msg warn">
          {tr({
            en: 'No match on the map. Check the address, or fill the fields below by hand.',
            ar: 'لا يوجد تطابق على الخريطة. راجع العنوان أو أكمل الحقول بالأسفل يدويًا.',
          })}
        </div>
      )}
      {status === 'error' && (
        <div className="addr-geo-msg error">
          {error || tr({ en: 'Address lookup failed', ar: 'تعذّر البحث عن العنوان' })}
        </div>
      )}
      {status === 'ok' && result && (
        <>
          <div className="addr-geo-msg ok">
            {pickName(result.formatted, lang) ??
              tr({ en: 'Address found', ar: 'تم العثور على العنوان' })}
          </div>
          {result.approximate && (
            <div className="addr-geo-msg warn">
              {tr({
                en: 'Approximate match — check the filled fields before saving.',
                ar: 'تطابق تقريبي — راجع الحقول قبل الحفظ.',
              })}
            </div>
          )}
        </>
      )}

      {result && <AddressMap lat={result.lat} lng={result.lng} />}
    </div>
  );
}

/**
 * The pin. A Static Maps image proxied through pos-api rather than an embedded
 * Maps JS map, for the same reason as the lookup: the key stays on the server.
 * Fetched with the bearer token, so it can't be a plain <img src>.
 *
 * The coordinates are not persisted — there is no lat/lng column on
 * Inv_CashCustomerDetail — so the pin shows for the address the cashier just
 * looked up, and comes back on the next lookup rather than on reopening.
 */
function AddressMap({ lat, lng }: { lat: number; lng: number }) {
  const { t: tr, lang } = useLang();
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    setFailed(false);
    setSrc(null);
    void geoAPI
      .staticMap({ lat, lng, w: 600, h: 200, lang })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [lat, lng, lang]);

  const mapsHref = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

  return (
    <div className="addr-geo-map">
      {src && <img src={src} alt={tr({ en: 'Map of the address', ar: 'خريطة العنوان' })} />}
      {!src && !failed && (
        <div className="addr-geo-map-skeleton">{tr({ en: 'Loading map…', ar: 'جارٍ تحميل الخريطة…' })}</div>
      )}
      <div className="addr-geo-map-foot">
        <span>{lat.toFixed(5)}, {lng.toFixed(5)}</span>
        <a href={mapsHref} target="_blank" rel="noopener noreferrer">
          {tr({ en: 'Open in Google Maps', ar: 'افتح في خرائط جوجل' })}
        </a>
      </div>
    </div>
  );
}

